import React, { useCallback, useEffect, useState } from 'react';
import Stage1Form from './components/scraper/Stage1Form';
import Stage2Table from './components/leads/Stage2Table';
import Stage3DMSettings from './components/settings/Stage3DMSettings';
import FbAdsScraper from './components/scraper/FbAdsScraper';
import Header from './components/layout/Header';
import Sidebar from './components/layout/Sidebar';
import Dashboard from './components/dashboard/Dashboard';
import LeadFinder from './components/leadFinder/LeadFinder';
import WhatsApp from './components/whatsapp/WhatsApp';
import AuthPage from './components/auth/AuthPage';
import { apiUrl } from './config';
import { hashForStage, pushStageHash, replaceStageHash, stageFromLocation } from './utils/navigation';
import './App.css';

function readStoredUser() {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

function clearSessionStorage() {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
}

function App() {
  const [activeStage, setActiveStage] = useState(() => stageFromLocation());
  const [refreshTable, setRefreshTable] = useState(false);
  // Mobile/tablet nav drawer. Ignored above the breakpoint, where the sidebar
  // is always visible.
  const [navOpen, setNavOpen] = useState(false);
  const [user, setUser] = useState(() => (localStorage.getItem('token') ? readStoredUser() : null));
  const [authChecking, setAuthChecking] = useState(() => Boolean(localStorage.getItem('token')));

  const goToStage = useCallback((stage) => {
    setActiveStage(stage);
    pushStageHash(stage);
  }, []);

  const handleLogout = useCallback(() => {
    const token = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refreshToken');
    // Best-effort server revoke; always clear local session.
    if (token) {
      fetch(apiUrl('/auth/logout'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ refreshToken }),
      }).catch(() => {});
    }
    clearSessionStorage();
    setUser(null);
    setActiveStage('dashboard');
    replaceStageHash('dashboard');
  }, []);

  const handleAuthSuccess = useCallback((nextUser) => {
    setUser(nextUser || readStoredUser());
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setAuthChecking(false);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(apiUrl('/auth/me'), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('unauthorized');
        const data = await res.json();
        if (!cancelled) {
          localStorage.setItem('user', JSON.stringify(data.user));
          setUser(data.user);
        }
      } catch (_) {
        if (!cancelled) handleLogout();
      } finally {
        if (!cancelled) setAuthChecking(false);
      }
    })();

    return () => { cancelled = true; };
  }, [handleLogout]);

  useEffect(() => {
    if (!navOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setNavOpen(false); };
    // Resizing past the breakpoint shows the sidebar normally; without this the
    // drawer state would stay "open" and reappear on the next resize down.
    const onResize = () => { if (window.innerWidth > 900) setNavOpen(false); };
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
    };
  }, [navOpen]);

  useEffect(() => {
    const onUnauthorized = () => handleLogout();
    window.addEventListener('vaanidm:unauthorized', onUnauthorized);
    return () => window.removeEventListener('vaanidm:unauthorized', onUnauthorized);
  }, [handleLogout]);

  useEffect(() => {
    // Redirect bare "/" to "/dashboard"
    if (window.location.pathname === '/') {
      window.history.replaceState(null, '', hashForStage(stageFromLocation()));
    }
    const syncFromUrl = () => setActiveStage(stageFromLocation());
    window.addEventListener('popstate', syncFromUrl);
    return () => {
      window.removeEventListener('popstate', syncFromUrl);
    };
  }, []);

  const handleScrapeSuccess = () => setRefreshTable((prev) => !prev);

  if (authChecking) {
    return (
      <div className="app-container" style={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
        <p style={{ color: '#6e7390' }}>Checking session…</p>
      </div>
    );
  }

  if (!user) {
    return <AuthPage onAuthSuccess={handleAuthSuccess} />;
  }

  return (
    <div className="app-container">
      <Header
        user={user}
        onLogout={handleLogout}
        onToggleNav={() => setNavOpen((open) => !open)}
        navOpen={navOpen}
      />
      <div className="content-wrapper">
        {/* Below the tablet breakpoint the sidebar becomes an overlay drawer;
            this backdrop both dims the page and gives a tap target to close
            it. It is display:none on larger screens. */}
        <div
          className={`nav-backdrop ${navOpen ? "open" : ""}`}
          onClick={() => setNavOpen(false)}
          aria-hidden="true"
        />
        <Sidebar
          currentStage={activeStage}
          setCurrentStage={(stage) => { goToStage(stage); setNavOpen(false); }}
          open={navOpen}
        />
        <main className="main-content">
          {activeStage === 'dashboard' && <Dashboard />}
          {activeStage === 'leadfinder' && <LeadFinder />}
          {activeStage === 'whatsapp' && <WhatsApp />}
          {activeStage === 'stage1' && <Stage1Form onScrapeSuccess={handleScrapeSuccess} />}
          {activeStage === 'stage4' && <FbAdsScraper />}
          {activeStage === 'stage2' && <Stage2Table refresh={refreshTable} />}
          {activeStage === 'stage3' && <Stage3DMSettings />}
        </main>
      </div>
    </div>
  );
}

export default App;
